import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, fmtMoney, CURRENCY_META } from "@/lib/api";
import { useI18n } from "@/i18n";
import { CreditCard, Plus, X, Loader2, Snowflake, Eye, EyeOff, Power } from "lucide-react";
import { Logo } from "@/components/Logo";

function IssueModal({ accounts, currencies, onClose, onIssued }) {
  const { t } = useI18n();
  const [accountId, setAccountId] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("virtual");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const curMap = Object.fromEntries(currencies.map((c) => [c.code, c]));

  const issue = async () => {
    if (!accountId || !name.trim()) return toast.error(t("common.error"));
    setBusy(true);
    try {
      await api.post("/cards", { account_id: accountId, cardholder_name: name, card_type: type, label });
      toast.success(t("cards.issued"));
      onIssued(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md ob-card rounded-md p-6" onClick={(e) => e.stopPropagation()} data-testid="issue-card-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-bold text-white">{t("cards.issueTitle")}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-zinc-500">{t("cards.linkedAccount")}</label>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="card-account-select"
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2.5 text-white focus:border-[#00E5FF] outline-none">
              <option value="">—</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{`${CURRENCY_META[a.currency_code]?.flag || ""} ${a.account_number} · ${fmtMoney(a.available_balance, curMap[a.currency_code]?.decimals || 2)}`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500">{t("cards.holderName")}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("cards.holderPh")} data-testid="card-name-input"
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2.5 text-white focus:border-[#00E5FF] outline-none" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">{t("cards.label")} ({t("common.optional")})</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="card-label-input"
              className="mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2.5 text-white focus:border-[#00E5FF] outline-none" />
          </div>
        </div>
        <button onClick={issue} disabled={busy} data-testid="confirm-issue-card"
          className="mt-6 w-full bg-[#00E5FF] text-black font-bold py-3 rounded-md hover:bg-[#33EBFF] flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={18} className="animate-spin" />} {t("cards.issueBtn")}
        </button>
      </div>
    </div>
  );
}

function CardVisual({ card, onChanged }) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  const frozen = card.status === "frozen";
  const closed = card.status === "closed";

  const fmtNum = (n) => (show ? n.replace(/(.{4})/g, "$1 ").trim() : "•••• •••• •••• " + n.slice(-4));

  const setStatus = async (status) => {
    try { await api.post(`/cards/${card.id}/status?status=${status}`); toast.success(t("cards.statusUpdated")); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`card-${card.card_number.slice(-4)}`} className={`rounded-xl p-5 relative overflow-hidden border ${closed ? "opacity-40" : ""} ${frozen ? "border-sky-500/40" : "border-zinc-800"}`}
      style={{ background: "linear-gradient(135deg, #0c1416 0%, #06181c 55%, #021014 100%)" }}>
      <div className="absolute -top-10 -end-10 w-40 h-40 rounded-full bg-[#00E5FF]/10 blur-2xl" />
      <div className="flex items-start justify-between mb-8 relative">
        <Logo size="sm" />
        <span className="text-xs font-semibold text-zinc-400">{card.card_type === "virtual" ? t("cards.virtual") : t("cards.physical")}</span>
      </div>
      <div className="font-mono-ob text-lg text-white tracking-wider mb-4" dir="ltr">{fmtNum(card.card_number)}</div>
      <div className="flex items-end justify-between relative" dir="ltr">
        <div>
          <div className="text-[10px] text-zinc-500 uppercase">{card.cardholder_name}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{t("cards.expires")} {String(card.expiry_month).padStart(2, "0")}/{String(card.expiry_year).slice(-2)} · {t("cards.cvv")} {show ? card.cvv : "•••"}</div>
          <div className="text-[10px] text-[#00E5FF] mt-0.5 font-mono-ob">{card.account_number}</div>
        </div>
        <span className="text-sm font-bold text-zinc-300 italic">{card.brand}</span>
      </div>
      {!closed && (
        <div className="flex items-center gap-2 mt-4 relative">
          <button onClick={() => setShow((s) => !s)} data-testid={`toggle-details-${card.card_number.slice(-4)}`}
            className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white">
            {show ? <EyeOff size={13} /> : <Eye size={13} />} {show ? t("cards.hideDetails") : t("cards.showDetails")}
          </button>
          <span className="text-zinc-700">|</span>
          {frozen ? (
            <button onClick={() => setStatus("active")} className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300"><Power size={13} /> {t("cards.unfreeze")}</button>
          ) : (
            <button onClick={() => setStatus("frozen")} data-testid={`freeze-${card.card_number.slice(-4)}`} className="flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300"><Snowflake size={13} /> {t("cards.freeze")}</button>
          )}
          <span className="text-zinc-700">|</span>
          <button onClick={() => setStatus("closed")} className="text-xs text-red-400 hover:text-red-300">{t("cards.closeCard")}</button>
        </div>
      )}
    </div>
  );
}

export default function Cards() {
  const { t } = useI18n();
  const [cards, setCards] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIssue, setShowIssue] = useState(false);

  const load = async () => {
    try {
      const [c, a, cu] = await Promise.all([api.get("/cards"), api.get("/accounts"), api.get("/currencies")]);
      setCards(c.data);
      setAccounts(a.data.filter((x) => x.status !== "closed"));
      setCurrencies(cu.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="w-48 ob-loader h-1 rounded-full bg-zinc-900 mx-auto mt-20" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3"><CreditCard className="text-[#00E5FF]" /> {t("cards.title")}</h1>
          <p className="text-zinc-500 text-sm mt-1">{t("cards.sub")}</p>
        </div>
        <button onClick={() => accounts.length ? setShowIssue(true) : toast.error(t("cards.noAccount"))} data-testid="issue-card-button"
          className="bg-[#00E5FF] text-black font-bold px-5 py-2.5 rounded-md hover:bg-[#33EBFF] flex items-center gap-2"><Plus size={18} /> {t("cards.issue")}</button>
      </div>

      {cards.length === 0 ? (
        <div className="ob-card rounded-md p-12 text-center">
          <CreditCard size={40} className="mx-auto text-zinc-700 mb-4" />
          <p className="text-zinc-400">{t("cards.empty")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-5" data-testid="cards-grid">
          {cards.map((c) => <CardVisual key={c.id} card={c} onChanged={load} />)}
        </div>
      )}

      {showIssue && <IssueModal accounts={accounts} currencies={currencies} onClose={() => setShowIssue(false)} onIssued={load} />}
    </div>
  );
}
